import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiEdit2, FiEye } from 'react-icons/fi';
import { Table, Button, Pagination, Select } from 'antd';
import DashboardPageWrapper from '../../../components/layout/DashboardPageWrapper';
import supplierService from '../../../services/supplierService';
import { useAuth } from '../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../config/permissions';
import { getSupplierProfilePath } from '../../product-master/pre-ipo/order-management/orderEntityLinks';

const { Option } = Select;

const ONBOARD_BASE = '/supplier-masters/onboard';
const SEARCH_DEBOUNCE_MS = 350;

export default function SupplierMastersListPage() {
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(PERMISSIONS.SUPPLIERS_MANAGE);

  const [suppliers, setSuppliers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [bankApprovalStatus, setBankApprovalStatus] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchDebounced(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const search = searchDebounced.length >= 3 ? searchDebounced : undefined;
      const { suppliers: list, pagination: pg } = await supplierService.listSuppliers({
        page,
        limit: pageSize,
        search,
        ...(bankApprovalStatus ? { bank_approval_status: bankApprovalStatus } : {}),
      });
      setSuppliers(Array.isArray(list) ? list : []);
      setPagination(pg);
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
      setSuppliers([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchDebounced, bankApprovalStatus]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const totalCount = pagination?.total ?? 0;

  const goToProfile = useCallback(
    (record) => {
      const profilePath = getSupplierProfilePath(record.uuid || record.id);
      if (profilePath) navigate(profilePath);
    },
    [navigate]
  );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 72,
      render: (id) => (
        <span className="font-mono text-green-700 font-medium px-2 py-0.5 bg-green-50 rounded text-xs">{id ?? '—'}</span>
      ),
    },
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goToProfile(record);
          }}
          className="font-semibold text-green-700 hover:text-green-800 hover:underline text-left"
          style={{ whiteSpace: 'nowrap' }}
          title={text}
        >
          {text || '—'}
        </button>
      ),
    },
    {
      title: 'PAN',
      dataIndex: 'pan',
      key: 'pan',
      render: (text) =>
        text ? (
          <span
            className="font-mono text-sm text-blue-700 px-2 py-0.5 bg-blue-50 border border-blue-100 rounded"
            style={{ whiteSpace: 'nowrap' }}
          >
            {text}
          </span>
        ) : (
          <span className="text-gray-400" style={{ whiteSpace: 'nowrap' }}>—</span>
        ),
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      render: (text) => (
        <span className="text-gray-700 text-sm" style={{ whiteSpace: 'nowrap' }}>
          {text || '—'}
        </span>
      ),
    },
    {
      title: 'PHONE',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => (
        <span className="text-gray-700 tracking-wide" style={{ whiteSpace: 'nowrap' }}>
          {text || '—'}
        </span>
      ),
    },
    {
      title: 'BANK',
      key: 'bank',
      render: (_, record) => {
        const line = [record.bank_name, record.account_number].filter(Boolean).join(' · ');
        return (
          <span className="text-sm text-gray-600" style={{ whiteSpace: 'nowrap' }}>
            {line || '—'}
          </span>
        );
      },
    },
    {
      title: 'DEMAT',
      key: 'demat',
      render: (_, record) => {
        const line = [record.dp_name, record.demat_number].filter(Boolean).join(' · ');
        return (
          <span className="text-sm text-gray-600" style={{ whiteSpace: 'nowrap' }}>
            {line || '—'}
          </span>
        );
      },
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 180,
      render: (_, record) => {
        const id = record.uuid ?? record.id ?? record.user_id;
        return (
          <div className="flex items-center gap-2">
            <Button
              size="small"
              type="default"
              className="flex items-center gap-1.5"
              icon={<FiEye size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                goToProfile(record);
              }}
            >
              View
            </Button>
            {canManage && id ? (
              <Button
                size="small"
                type="default"
                className="flex items-center gap-1.5 border-green-600 text-green-700 hover:text-white hover:bg-green-600 hover:border-green-600 font-medium"
                icon={<FiEdit2 size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`${ONBOARD_BASE}/${id}?edit=1`);
                }}
              >
                Edit
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Supplier Masters</h1>
          <p className="text-sm text-gray-500 mt-1">View supplier profiles, edit details in the wizard, or start a new onboarding.</p>
        </div>

        {canManage && (
          <button
            type="button"
            onClick={() => navigate(ONBOARD_BASE)}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 active:scale-[0.99] transition-all shadow-sm"
          >
            <FiPlus size={16} /> Onboard new supplier
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3 bg-gray-50/50">
          <div className="relative w-full sm:max-w-md">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or PAN (min 3 characters)"
              value={searchInput}
              onChange={handleSearchChange}
              className="w-full h-[38px] pl-9 pr-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all bg-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Bank review:</span>
            <Select
              value={bankApprovalStatus || 'all'}
              onChange={(val) => {
                setBankApprovalStatus(val === 'all' ? '' : val);
                setPage(1);
              }}
              className="w-[150px] h-[38px]"
            >
              <Option value="all">All</Option>
              <Option value="pending_review">Pending review</Option>
            </Select>
          </div>
          <p className="text-xs text-gray-500 sm:ml-auto">
            {totalCount} suppliers
            {searchDebounced.length >= 3 ? ' (filtered)' : ''}
          </p>
        </div>

        <div className="overflow-x-auto">
          <Table
            columns={columns}
            dataSource={suppliers}
            rowKey={(r) => r.uuid || r.id}
            loading={loading}
            pagination={false}
            className="custom-table min-w-[960px]"
            rowClassName="hover:bg-gray-50 transition-colors cursor-pointer"
            onRow={(record) => ({
              onClick: () => goToProfile(record),
            })}
          />
        </div>

        <div className="flex items-center justify-end px-4 py-3 border-t border-gray-100 bg-white">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={totalCount}
            showSizeChanger
            pageSizeOptions={['10', '15', '30', '50']}
            onChange={(p, ps) => {
              setPage(p);
              if (ps !== pageSize) {
                setPageSize(ps);
                setPage(1);
              }
            }}
            onShowSizeChange={(_, ps) => {
              setPageSize(ps);
              setPage(1);
            }}
            showTotal={(total, range) => (
              <span className="text-xs text-gray-500">
                {range[0]}–{range[1]} of {total}
              </span>
            )}
          />
        </div>
      </div>
    </DashboardPageWrapper>
  );
}
